import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { StringStringTuple, ReportMetadataService, AspectGroupViewModel, AspectGroupService, Int32StringTuple } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';

@Component({
    selector: 'app-aspect-group-details',
    templateUrl: './aspect-group-details.component.html',
    styleUrls: ['./aspect-group-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AspectGroupDetailsComponent implements OnInit, OnChanges, CanComponentDeactivate  {

    @ViewChild('form', { static: true }) form: NgForm;
    @ViewChild('app-aspect-list', { static: true })

    aspectGroup: AspectGroupViewModel;
    users$: Observable<Array<StringStringTuple>>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;

    id: number;
    aspectGroupForm: UntypedFormGroup;
    public isNew = false;
    private isSaving = false;
    pageTitle: string = "Aspektgrupp - Detaljer";

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private aspectGroupService: AspectGroupService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
    ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('aspectGroupId'));
    }

    ngOnInit(): void {
        this.buildForm();

        this.users$ = this.reportMetadataService.getUserMetadata();

        if (this.id) {
            this.aspectGroupService.getAspectGroupById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
        } else {
            this.isNew = true;
            this.aspectGroup = this.initializeEmptyObject();
            this.resetForm();
        }
    }

    ngOnChanges(): void {
        if (this.aspectGroup) {
            this.isNew = false;
        }
        else {
            this.isNew = true;
            this.aspectGroup = this.initializeEmptyObject();
        }

        this.resetForm();
    }


    dataLoaded(aspectGroup: AspectGroupViewModel) {
        this.aspectGroup = aspectGroup;
        this.pageTitle = "Aspektgrupp - " + aspectGroup.name;

        this.isNew = false;
        this.resetForm();
    }

    public getFormControl(name: string): AbstractControl {
        return this.aspectGroupForm.controls[name];
    }


    private buildForm() {
        this.aspectGroupForm = this.formBuilder.group({
            aspectGroupId: '',
            name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            enabled: '',
            description: '',
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
        });
    }

    public resetForm() {
        if (!this.aspectGroup) {
            this.isNew = true;
            this.aspectGroup = this.initializeEmptyObject();
        }

        this.aspectGroupForm.reset({
            aspectGroupId: this.aspectGroup.aspectGroupId || '',
            name: this.aspectGroup.name || '',
            enabled: this.aspectGroup.enabled || '',
            description: this.aspectGroup.description || '',
            createdBy: this.aspectGroup.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.aspectGroup.createdDate, true) || '',
            updatedBy: this.aspectGroup.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.aspectGroup.updatedDate, true) || '',
        });

        if (this.aspectGroup.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.aspectGroup.createdBy).pipe(map(t => t.item2));
        }
        if (this.aspectGroup.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.aspectGroup.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): AspectGroupViewModel {
        var result = <AspectGroupViewModel>{};
        result.enabled = true;

        return result;
    }

    private prepareSave(): AspectGroupViewModel {
        const formModel = this.aspectGroupForm.value;

        return {
            aspectGroupId: this.isNew ? null : formModel.aspectGroupId,
            name: formModel.name,
            enabled: formModel.enabled ? true : false,
            description: formModel.description,
        };
    }

    public save() {
        if (!this.aspectGroupForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const aspectGroup = this.prepareSave();

        if (this.isNew) {
            this.aspectGroupService.createAspectGroup(aspectGroup).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
        else {
            this.aspectGroupService.updateAspectGroup(aspectGroup.aspectGroupId, aspectGroup).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.location.back();
    }

    // Prevent leaving the form accidentaly if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.aspectGroupForm.dirty) {
            let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return close.pipe(
                merge(remain),
                take(1));

        } else {
            return of(true);
        }
    }

    private saveCompleted(aspectGroup: AspectGroupViewModel) {
        this.aspectGroup = aspectGroup;
        this.id = aspectGroup.aspectGroupId;
        this.isNew = false;

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), aspectGroup.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }
}
